import { useState, useEffect, useMemo } from 'react';
import {
  Page,
  Column,
  Row,
  Spacer,
  Flex,
  Empty,
} from '@oliasoft-open-source/react-ui-library';
import { DataDrawer } from 'client/components/data-viewer/data-drawer';
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import {
  createNode,
  deleteNode,
  updateNode,
} from 'client/store/entities/hierarchy/node/node';
import { getArchives } from 'client/store/entities/hierarchy/archive/archive';
import { RootState } from 'client/store/configureStore';
import {
  HierarchyLevelType,
  INode,
  IObject,
} from 'common/interfaces/hierarchy-entities';
import {
  setSelectedArchiveId,
  setActiveItem,
  setAfterUnpackedActiveArchiveState,
} from 'client/store/ui/hierarchy-tree/hierarchy-tree';
import { useParams, useSearchParams } from 'react-router-dom';
import { DeleteModal } from 'client/components/delete.modal';
import {
  getCreateEntityAction,
  getDefaultNewEntity,
  getOneEntityAction,
  getUpdateEntityAction,
  getManyEntitiesAction,
  mapTreeItemToEntity,
  deleteEntityAction,
} from 'client/views/explore/hierarchy/hierarchy-entities-factory';
import { HierarchyBreadcrumb } from 'client/views/explore/hierarchy/hierarchy-breadcrumb';
import { IHierarchyTreeItem } from 'client/views/explore/hierarchy/hierarchy.interfaces';
import { HierarchyItemStates } from 'client/views/explore/hierarchy/hierarchy-enums';
import { EntityFormModal } from 'client/views/explore/hierarchy-forms/entity-form';
import { GroupCompanyDataBy } from 'client/views/company-data/company-data.interfaces';
import { hierarchyReset } from 'client/store/entities/hierarchy-state-slice-factory';
import { combineCompanyData } from './company-data-drawer/company-data-drawer.utils';
import { CompanyDataDrawer } from './company-data-drawer/company-data-drawer';
import { DataExplorer } from './company-data-explorer/data-explorer';
import { generateHierarchyTree } from '../explore/hierarchy/hierarchy.utils';
import { getObjectsOfNode } from '../../store/entities/object/object';
import { IPMFilesExplorer } from './ipm-files/ipm-files';

const CompanyData = ({
  archives,
  getArchives,
  nodes,
  objects,
  createNode,
  nodesPages,
  getObjectsOfNode,
  deleteEntityAction,
  selectedArchiveId,
  setSelectedArchiveId,
  activeItem,
  setActiveItem,
  selectedCustomer,
}: PropsFromRedux) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedData, setSelectedData] = useState<IObject | null>(null);
  const [dataViewerVisible, setDataViewerVisible] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupCompanyDataBy>(
    GroupCompanyDataBy.Folder,
  );

  // @ts-ignore
  const isIPMSettignsNode = activeItem?.nodeid === '-1';

  const getNodeObjectsCount = (nodeid: string): number => {
    return nodes.reduce((acc: number, child: INode) => {
      if (child.parentid === nodeid) {
        return acc + getNodeObjectsCount(child.nodeid);
      }
      return acc;
    }, objects?.[nodeid]?.length || 0);
  };

  const setHierarchyItem = (item: IHierarchyTreeItem) => {
    const { onClick, ...itemCLean } = item;
    setActiveItem(itemCLean);
    setSelectedData(null);
  };

  const hierarchyTree = useMemo(() => {
    if (!archives) return;
    const combinedHierarchy = combineCompanyData(archives, nodes);

    return generateHierarchyTree(combinedHierarchy, setHierarchyItem);
  }, [archives, nodes]);

  const [loadedNodes, setLoadedNodes] = useState<{ [key: string]: true }>({});

  useEffect(() => {
    const nodesIds = nodes.map((node: { nodeid: string }) => node.nodeid);

    const loadedNow: any = {};
    nodesIds.forEach((nodeId: string) => {
      loadedNow[nodeId] = true;
      if (loadedNodes[nodeId]) return;
      getObjectsOfNode(nodeId);
    });
    setLoadedNodes({ ...loadedNow });
  }, [nodes]);

  const loadMoreChildren = (parentItem: Pick<IHierarchyTreeItem, 'id' | 'entityType' | 'nodeId'>) => {
    const parentId = parentItem.id || '';
    const loadType = HierarchyLevelType.Node;
    const params = {
      parentId,
      parenttype: HierarchyLevelType.Archive,
      page: nodesPages[parentId] || 1,
    };

    dispatch(getManyEntitiesAction(loadType, params));
  };

  useEffect(() => {
    if (!selectedCustomer || selectedCustomer.companyId === companyId) {
      setSelectedArchiveId('');
      getArchives();
      const activeArhiveId = searchParams.get('activeArhiveId');
      const activeEntityType = searchParams.get('activeEntityType');
      const activeEntityId = searchParams.get('activeEntityId');

      if (!activeArhiveId || !activeEntityId || !activeEntityType) return;
      setSelectedArchiveId(activeArhiveId);
    }
  }, [selectedCustomer, companyId]);

  useEffect(() => {
    if (!archives) return;
    if (selectedArchiveId) {
      setSearchParams({
        activeArhiveId: selectedArchiveId,
        activeEntityId: selectedArchiveId,
        activeEntityType: HierarchyLevelType.Archive,
      });
      return;
    }

    setSelectedArchiveId(archives[0]?.archiveid);
  }, [archives, selectedArchiveId]);

  useEffect(() => {
    if (!hierarchyTree || !activeItem) return;
    setSearchParams({
      activeArhiveId: selectedArchiveId!,
      activeEntityId: activeItem.id,
      activeEntityType: activeItem.entityType,
    });
  }, [activeItem, hierarchyTree]);

  useEffect(() => {
    if (!selectedArchiveId || !selectedCustomer) return;
    hierarchyReset();
    loadMoreChildren({
      id: selectedArchiveId,
      entityType: HierarchyLevelType.Archive,
    });
  }, [selectedArchiveId, selectedCustomer]);

  const [prevActiveId, setPrevActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (!activeItem || !hierarchyTree || activeItem.id === prevActiveId || activeItem.entityType === HierarchyLevelType.Archive || !hierarchyTree[activeItem.id]) return;
    setPrevActiveId(activeItem.id);
  }, [activeItem, hierarchyTree]);

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [editingItemParent, setEditingItemParent] = useState<IHierarchyTreeItem | null>(null);
  const [itemState, setItemState] = useState<HierarchyItemStates | null>(null);

  const createItem = (
    entityType: HierarchyLevelType,
    parentItem: IHierarchyTreeItem | null,
  ) => {
    const newDefaultItem = getDefaultNewEntity(entityType, parentItem?.id);
    setEditingItemParent(parentItem);
    setSelectedItem(newDefaultItem);
    setItemState(HierarchyItemStates.Create);
  };

  const editItem = (item: IHierarchyTreeItem) => {
    setSelectedItem(item);
    setItemState(HierarchyItemStates.Edit);
  };

  const deleteItem = (item: IHierarchyTreeItem) => {
    setSelectedItem(item);
    setItemState(HierarchyItemStates.Delete);
  };

  const empty = (
    <Flex alignItems="center" justifyContent="center">
      <Empty text="Select an item on the left to view files" />
    </Flex>
  );
  const isEditItem = itemState === HierarchyItemStates.Edit;
  const isCreateItem = itemState === HierarchyItemStates.Create;
  const isDeleteItem = itemState === HierarchyItemStates.Delete;

  const saveEntity = async (entity: {
    type: string;
    payload: Partial<IHierarchyTreeItem>;
  }) => {
    if (!hierarchyTree) return;

    if (isEditItem) {
      dispatch(
        getUpdateEntityAction({
          ...entity.payload,
          entityType: entity.type as HierarchyLevelType,
        })!,
      );
    } else if (entity.type === HierarchyLevelType.Node) {
      const isParentNode = editingItemParent!.entityType === HierarchyLevelType.Node;
      const newNode = mapTreeItemToEntity({
        ...entity.payload,
        ...(isParentNode && { parentid: editingItemParent!.id }),
        ...(!isParentNode && {
          parent: {
            type: editingItemParent!.entityType,
            id: editingItemParent!.id,
          },
        }),
      });

      createNode(newNode, () => {
        if (!isParentNode) {
          dispatch(getOneEntityAction(editingItemParent!));
        }
      });
    } else {
      dispatch(
        getCreateEntityAction({
          ...entity.payload,
          entityType: entity.type as HierarchyLevelType,
        }),
      );
    }
    setSelectedItem(null);
  };

  const closeModal = () => {
    setItemState(null);
    setSelectedItem(null);
  };

  const onDelete = () => {
    if (!hierarchyTree) return;
    const { entityType } = selectedItem!;

    deleteEntityAction(selectedItem!);

    if (entityType === HierarchyLevelType.Archive) {
      setSelectedArchiveId(null);
    }

    closeModal();
  };

  const renderDataExplorer = () => {
    if (activeItem) {
      if (isIPMSettignsNode) {
        return (
          <IPMFilesExplorer
            selectedData={selectedData}
            // @ts-ignore
            setSelectedData={setSelectedData}
            setDataViewerVisible={setDataViewerVisible}
          />
        );
      }
      return (
        <DataExplorer
          selectedData={selectedData}
          setSelectedData={setSelectedData}
          hierarchy={hierarchyTree || {}}
          deleteItem={deleteItem}
          setDataViewerVisible={setDataViewerVisible}
          getNodeChildrenCount={getNodeObjectsCount}
        />
      );
    }
    return empty;
  };

  return (
    <Page padding={false} scroll={false}>
      { (isCreateItem || isEditItem) && selectedItem && <EntityFormModal entity={selectedItem} isEdit={isEditItem} saveEntity={saveEntity} close={closeModal} />}
      { isDeleteItem && selectedItem && <DeleteModal entity={selectedItem} onSubmit={onDelete} onClose={closeModal} />}

      <Row spacing={0} height="100%">
        <CompanyDataDrawer
          hierarchy={hierarchyTree || {}}
          groupBy={groupBy}
          setGroupBy={(groupBy: GroupCompanyDataBy) => {
            setGroupBy(groupBy);
          }}
          createItem={createItem}
          editItem={editItem}
          onItemDelete={deleteItem}
          archives={archives || []}
          getNodeObjectsCount={getNodeObjectsCount}
        />
        <Column padding="var(--padding)" scroll>
          <Row>
            <Column padding="var(--padding-xs)">
              <HierarchyBreadcrumb
                hierarchy={hierarchyTree || {}}
                selectedItemId={activeItem?.id || null}
                topLevel={HierarchyLevelType.Archive}
                onClick={setHierarchyItem}
              />
            </Column>
          </Row>
          <Spacer />
          {renderDataExplorer()}
        </Column>
        <DataDrawer
          dataObject={selectedData}
          visible={dataViewerVisible}
          setVisible={setDataViewerVisible}
          setSelectedData={setSelectedData}
          archiveid={selectedArchiveId!}
        />
      </Row>
    </Page>
  );
};

const mapStateToProps = ({ entities, ui }: RootState) => ({
  archives: entities?.archive.archives,
  selectedCustomer: entities?.customer.selectedCustomer,
  nodes: entities?.nodeTree.items,
  objects: entities?.object.objects,
  nodesPages: entities?.nodeTree.pages,
  selectedArchiveId: ui.hierarchyTree.selectedArchiveId,
  activeItem: ui.hierarchyTree.activeItem,
});

const mapDispatchToProps = {
  hierarchyReset,
  createNode,
  deleteNode,
  updateNode,
  getObjectsOfNode,
  deleteEntityAction,
  setSelectedArchiveId,
  getArchives,
  setActiveItem,
  setAfterUnpackedActiveArchiveState,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(CompanyData);

export { Container as CompanyData };
